// Org-team role metadata + helpers (handoff from TestoLogics, 2026-06-13).
//
// Worksnip's user-level `role` (owner/admin/manager/member/viewer) is the
// work-management authority axis. It is ORTHOGONAL to `seat_type`
// (creator/collaborator/viewer = content-creation capability). A user can be a
// 'manager' on a 'viewer' seat (assigns + tracks work, doesn't author) or a
// 'member' on a 'creator' seat (authors content, no work-management authority).

export type OrgRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';

export interface RoleMeta {
  value: OrgRole;
  /** i18next KEY, not display text — this file has no React context to call
   *  useTranslation() itself. The consumer (AdminUsers.tsx) renders via
   *  t(r.label) / t(r.description). */
  label: string;
  description: string;
}

// Order = authority, highest first. Used for dropdowns + the legend.
export const ROLE_META: RoleMeta[] = [
  { value: 'owner',   label: 'admin:users.roleLegend.roles.owner.label',   description: 'admin:users.roleLegend.roles.owner.description' },
  { value: 'admin',   label: 'admin:users.roleLegend.roles.admin.label',   description: 'admin:users.roleLegend.roles.admin.description' },
  { value: 'manager', label: 'admin:users.roleLegend.roles.manager.label', description: 'admin:users.roleLegend.roles.manager.description' },
  { value: 'member',  label: 'admin:users.roleLegend.roles.member.label',  description: 'admin:users.roleLegend.roles.member.description' },
  { value: 'viewer',  label: 'admin:users.roleLegend.roles.viewer.label',  description: 'admin:users.roleLegend.roles.viewer.description' },
];

export const ROLE_LABELS: Record<OrgRole, string> =
  Object.fromEntries(ROLE_META.map(r => [r.value, r.label])) as Record<OrgRole, string>;

type RoleUser = {
  role?: string | null;
  is_org_admin?: boolean | null;
  is_super_admin?: boolean | null;
} | null | undefined;

/** True when the user holds work-management authority (assign + track), via any
 *  of: super-admin, org-admin flag, or role owner/admin/manager. Mirrors the
 *  backend requireManager gate. */
export function isManagerUser(user: RoleUser): boolean {
  if (!user) return false;
  if (user.is_super_admin || user.is_org_admin) return true;
  return user.role === 'owner' || user.role === 'admin' || user.role === 'manager';
}

/** True when the user has full workspace-admin authority (users/billing/settings).
 *  Mirrors the backend requireWorkspaceAdmin gate. A Work Manager is NOT a
 *  workspace admin — that's the whole point of the role. */
export function isWorkspaceAdminUser(user: RoleUser): boolean {
  if (!user) return false;
  if (user.is_super_admin || user.is_org_admin) return true;
  return user.role === 'owner' || user.role === 'admin';
}

/** A "pure" manager — work authority WITHOUT workspace-admin authority. Used to
 *  show the manager-only slice of the admin surface (cohorts/teams + analytics)
 *  and hide users/billing/settings. */
export function isPureManager(user: RoleUser): boolean {
  return isManagerUser(user) && !isWorkspaceAdminUser(user);
}
